import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import CommunityBreadcrumb from '../components/community/CommunityBreadcrumb';
import { RegisteredUsersCount } from '../components/community/CommunityTopCreatorsRow';
import { CreatorSummaryTileButton } from '../components/community/CreatorSummaryTile';
import PageTemplate from '../components/layout/PageTemplate';
import { listCommunityTopCreators } from '../services/buildApi';
import { NAV_COMMUNITY, NAV_CREATORS, navLabel } from '../utils/communityNavStrings';
import './Community.css';
import './MyBuilds.css';

const PAGE_SIZE = 32;
const DIRECTORY_MIN_PLACEHOLDER_SLOTS = 20;

const TXT = {
    loading: { fr: 'Chargement...', en: 'Loading...', de: 'Wird geladen...', es: 'Cargando...', pt: 'Carregando...' },
    empty: { fr: 'Aucun créateur pour le moment.', en: 'No creators yet.', de: 'Noch keine Ersteller.', es: 'Ningún creador por ahora.', pt: 'Nenhum criador por enquanto.' },
    loadingMore: { fr: 'Chargement...', en: 'Loading more...', de: 'Wird geladen...', es: 'Cargando...', pt: 'Carregando...' },
};

function t(dict, lang) {
    return dict[lang] || dict.en;
}

const CommunityCreatorsDirectory = ({ lang = 'fr' }) => {
    const navigate = useNavigate();
    const [creators, setCreators] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const pageRef = useRef(0);
    const hasMoreRef = useRef(true);
    const loadingMoreRef = useRef(false);
    const observerRef = useRef();

    const appendNext = useCallback(async () => {
        if (loadingMoreRef.current || !hasMoreRef.current) return;
        loadingMoreRef.current = true;
        setLoadingMore(true);
        try {
            const nextPage = pageRef.current + 1;
            const data = await listCommunityTopCreators({ page: nextPage, page_size: PAGE_SIZE });
            const batch = Array.isArray(data?.results) ? data.results : [];
            pageRef.current = nextPage;
            const hm = !!data?.has_more;
            hasMoreRef.current = hm;
            setHasMore(hm);
            setCreators((prev) => [...prev, ...batch]);
        } catch {
            hasMoreRef.current = false;
            setHasMore(false);
        } finally {
            loadingMoreRef.current = false;
            setLoadingMore(false);
        }
    }, []);

    const appendNextRef = useRef(appendNext);
    useEffect(() => {
        appendNextRef.current = appendNext;
    }, [appendNext]);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        pageRef.current = 0;
        hasMoreRef.current = true;
        listCommunityTopCreators({ page: 1, page_size: PAGE_SIZE })
            .then((data) => {
                if (cancelled) return;
                setCreators(Array.isArray(data?.results) ? data.results : []);
                setTotal(Number(data?.total) || 0);
                pageRef.current = 1;
                const hm = !!data?.has_more;
                hasMoreRef.current = hm;
                setHasMore(hm);
            })
            .catch(() => {
                if (!cancelled) {
                    setCreators([]);
                    setTotal(0);
                    hasMoreRef.current = false;
                    setHasMore(false);
                }
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, []);

    const lastCreatorRef = useCallback(
        (node) => {
            if (observerRef.current) {
                observerRef.current.disconnect();
                observerRef.current = undefined;
            }
            if (!node || loading || !hasMore) return;
            observerRef.current = new IntersectionObserver(
                (entries) => {
                    if (!entries[0]?.isIntersecting) return;
                    if (observerRef.current) {
                        observerRef.current.disconnect();
                        observerRef.current = undefined;
                    }
                    appendNextRef.current();
                },
                { rootMargin: '200px' },
            );
            observerRef.current.observe(node);
        },
        [loading, hasMore],
    );

    useEffect(
        () => () => {
            observerRef.current?.disconnect();
        },
        [],
    );

    const openCreator = (c) => {
        if (!c?.public_slug) return;
        navigate(`/${lang}/community/${encodeURIComponent(c.public_slug)}`);
    };

    const creatorsDisplay = useMemo(() => {
        if (creators.length >= DIRECTORY_MIN_PLACEHOLDER_SLOTS) return creators;
        const r = [...creators];
        while (r.length < DIRECTORY_MIN_PLACEHOLDER_SLOTS) r.push(null);
        return r;
    }, [creators]);

    let lastRealIndex = -1;
    creatorsDisplay.forEach((c, i) => {
        if (c) lastRealIndex = i;
    });

    const title = navLabel(NAV_CREATORS, lang);

    return (
        <PageTemplate
            header={(
                <div className="community-creators-directory-header">
                    <CommunityBreadcrumb
                        className="community-heading-trail--flush"
                        crumbs={[
                            { label: navLabel(NAV_COMMUNITY, lang), to: `/${lang}/community` },
                            { label: title },
                        ]}
                    />
                    <RegisteredUsersCount total={total} lang={lang} />
                </div>
            )}
        >
            <div className="community-container community-creators-directory mybuilds-container">
            {loading ? (
                <div className="mybuilds-empty">
                    <p>{t(TXT.loading, lang)}</p>
                </div>
            ) : creators.length === 0 ? (
                <div className="mybuilds-empty">
                    <p>{t(TXT.empty, lang)}</p>
                </div>
            ) : (
                <>
                    <div className="community-creators-directory-grid">
                        {creatorsDisplay.map((c, idx) => (
                            <div
                                key={c ? c.public_slug : `ph-${idx}`}
                                className="community-creators-directory-item"
                                ref={c && idx === lastRealIndex ? lastCreatorRef : null}
                            >
                                <CreatorSummaryTileButton creator={c} lang={lang} onSelect={openCreator} />
                            </div>
                        ))}
                    </div>
                    {loadingMore ? (
                        <p className="community-creators-loading-more">{t(TXT.loadingMore, lang)}</p>
                    ) : null}
                </>
            )}
        </div>
        </PageTemplate>
    );
};

export default CommunityCreatorsDirectory;
